/* eslint-disable no-constant-condition */
import { Alert, Box, Card, Snackbar } from '@mui/material';
import React, { useEffect, useState } from 'react';
import CoursesApi from '../../../../../apis/CoursesApi';
import QuestionsApi from '../../../../../apis/QuestionsApi';
import LoadingCircle from '../../../../../components/LoadingCircle/LoadingCircle';
import UnexpectedError from '../../../../../components/UnexpectedError/UnexpectedError';
import { QuizModuleDto } from '../../../../../generated-types/quizModuleDto';
import useApi from '../../../../../lib/useApi';
import BaseModuleProps from '../../../lib/BaseModuleProps';
import QuestionCard from './components/QuestionCard';
import QuizCompleteCard from './components/QuizCompleteCard';
import QuizIntroCard from './components/QuizIntroCard';

/**
 * A module to assess the user on what they've learned
 */
const QuizModule = (props: BaseModuleProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizModule, setQuizModule] = useState<QuizModuleDto>();
  const [quizCard, setQuizCard] = useState<React.JSX.Element>();
  const [initialAnsweredQuestions, setInitialAnsweredQuestions] =
    useState<number>();
  const coursesApi = useApi(CoursesApi);
  const questionsApi = useApi(QuestionsApi);
  const [prevQuestionCorrect, setPrevQuestionCorrect] = useState<boolean>();
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    const load = async () => {
      // Start the requests
      const loadModulePromise = coursesApi.GetModule<QuizModuleDto>(
        props.moduleId
      );
      const loadAnsweredQuestionPromise = questionsApi.AnsweredQuestionCount(
        props.moduleId
      );

      // Wait for the requests to complete
      setQuizModule(await loadModulePromise);
      setInitialAnsweredQuestions(await loadAnsweredQuestionPromise);
    };

    // Loading is officially turned off in the useEffect below
    load().catch(() => setIsLoading(false));
  }, [coursesApi, props.moduleId, questionsApi]);

  useEffect(() => {
    if (quizModule && initialAnsweredQuestions) {
      setIsLoading(false);

      // Show the quiz complete card
      if (initialAnsweredQuestions >= quizModule.questionCountMinimum) {
        setQuizCard(<QuizCompleteCard nextModule={props.nextModule} />);
      }
      // Show the quiz intro card
      else if (!quizStarted) {
        setQuizCard(
          <QuizIntroCard
            minimumAnswerCount={quizModule.questionCountMinimum}
            quizTitle={quizModule.title}
            startQuiz={() => setQuizStarted(true)}
          />
        );
      }
      // Show the question card
      else {
        setQuizCard(
          <QuestionCard
            moduleId={props.moduleId}
            questionCountMinimum={quizModule.questionCountMinimum}
            setComplete={() => {
              props.setComplete();
              setInitialAnsweredQuestions(quizModule.questionCountMinimum);
            }}
            initialAnsweredQuestions={initialAnsweredQuestions}
            setPrevQuestionCorrect={setPrevQuestionCorrect}
            showFeedback={() => setShowFeedback(true)}
          />
        );
      }
    }
  }, [quizModule, initialAnsweredQuestions, quizStarted, props]);

  return (
    <Box
      position="relative"
      height="100%"
      width="100%"
      display="flex"
      justifyContent="center"
      alignItems="center"
      overflow="auto"
      padding=".5em"
    >
      {quizModule && initialAnsweredQuestions ? (
        <Card
          sx={{
            margin: 'auto',
            '@keyframes animatedBorder': {
              '0%': {
                backgroundPosition: '0% 50%',
              },
              '50%': {
                backgroundPosition: '100% 50%',
              },
              '100%': {
                backgroundPosition: '0% 50%',
              },
            },
            background:
              'linear-gradient(-70deg, #c2185b, #512da8, #1976d2, #0097a7)',
            backgroundSize: '400% 400%',
            animation: 'animatedBorder 10s ease infinite',
            padding: '5px',
            width: {
              md: '900px',
              xs: '100%',
            },
          }}
        >
          {quizCard}
        </Card>
      ) : !isLoading ? (
        <UnexpectedError errorContext="the quiz module" />
      ) : (
        <></>
      )}
      <LoadingCircle isLoading={isLoading} />
      <Snackbar
        data-testid="question-snackbar-id"
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom',
        }}
        open={showFeedback}
        onClose={() => setShowFeedback(false)}
        autoHideDuration={2000}
        sx={{
          position: 'absolute',
          left: '50%',
          bottom: '5px',
          transform: 'translateX(-50%)',
        }}
      >
        <Alert
          severity={prevQuestionCorrect ? 'success' : 'error'}
          variant="filled"
        >
          {prevQuestionCorrect ? 'Correct!' : 'Incorrect! Showing new question'}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuizModule;
